import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../hooks/useTheme';
import type { CatalogFlowCourseOption } from '../../utils/catalogFlowCourse';
import { catalogFlowFilterRowClass, catalogFlowPillTrackClass } from './catalogFlowFilterStyles';

type ScopeItem = { courseId: number | null; title: string };

type Props = {
  mode: 'quiz' | 'exercise';
  courseOptions: CatalogFlowCourseOption[];
  courseFilter: number | null;
  onCourseFilterChange: (courseId: number | null) => void;
};

/**
 * Course scope strip — same row / pill track size as status filter; courses scroll inside the track.
 */
export const CatalogFlowCourseScopeStrip: React.FC<Props> = ({
  mode,
  courseOptions,
  courseFilter,
  onCourseFilterChange
}) => {
  const { t, i18n } = useTranslation('learning');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const { isDark } = useTheme();
  const labelId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const pillContainerRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const [fitsInTrack, setFitsInTrack] = useState(true);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  const items = useMemo<ScopeItem[]>(
    () => [{ courseId: null, title: t('catalogFlow.filter.allCourses') }, ...courseOptions],
    [courseOptions, i18n.language]
  );

  const activeIndex = useMemo(() => {
    const idx = items.findIndex((it) => it.courseId === courseFilter);
    return idx >= 0 ? idx : 0;
  }, [items, courseFilter]);

  const indicatorColor =
    mode === 'quiz'
      ? isDark
        ? 'rgb(225 29 72)'
        : 'rgb(225 29 72)'
      : isDark
        ? 'rgb(217 119 6)'
        : 'rgb(217 119 6)';

  const updateIndicator = useCallback(() => {
    const container = pillContainerRef.current;
    const active = chipRefs.current[activeIndex];
    if (!container || !active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    setIndicatorStyle({
      left: aRect.left - cRect.left,
      width: aRect.width
    });
  }, [activeIndex]);

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setFitsInTrack(max <= 4);
    setOverflow({
      left: el.scrollLeft > 4,
      right: el.scrollLeft < max - 4
    });
  }, []);

  useEffect(() => {
    updateIndicator();
    updateOverflow();
    const el = scrollRef.current;
    if (!el) return;

    let hideTimer: number | null = null;
    const showScrollbar = () => {
      el.classList.add('scrolling');
      if (hideTimer != null) window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => el.classList.remove('scrolling'), 700);
    };

    const onScroll = () => {
      updateOverflow();
      updateIndicator();
      showScrollbar();
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('pointerdown', showScrollbar, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
      updateOverflow();
      updateIndicator();
    }) : null;
    ro?.observe(el);
    if (pillContainerRef.current) ro?.observe(pillContainerRef.current);

    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', showScrollbar);
      ro?.disconnect();
      if (hideTimer != null) window.clearTimeout(hideTimer);
    };
  }, [updateIndicator, updateOverflow, items.length, activeIndex]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const chip = chipRefs.current[activeIndex];
    if (!el || !chip) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;

    const elRect = el.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const chipCenter = chipRect.left + chipRect.width / 2 - elRect.left + el.scrollLeft;
    const target = chipCenter - el.clientWidth / 2;
    const clamped = Math.max(0, Math.min(target, maxScroll));

    el.scrollTo({ left: clamped, behavior: 'smooth' });
    const t = window.setTimeout(updateIndicator, 400);
    return () => clearTimeout(t);
  }, [activeIndex, updateIndicator]);

  const scrollByButton = useCallback((direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.65), behavior: 'smooth' });
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (items.length <= 1) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = (activeIndex + 1) % items.length;
        const item = items[next];
        if (item) onCourseFilterChange(item.courseId);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = (activeIndex - 1 + items.length) % items.length;
        const item = items[next];
        if (item) onCourseFilterChange(item.courseId);
      }
    },
    [activeIndex, items, onCourseFilterChange]
  );

  if (items.length <= 1) return null;

  const arrowInset = overflow.left || overflow.right;

  return (
    <div className={`${catalogFlowFilterRowClass} md:justify-center`}>
      <div
        className={`${catalogFlowPillTrackClass} overflow-hidden`}
        onKeyDown={onKeyDown}
      >
        {overflow.left && (
          <button
            type="button"
            onClick={() => scrollByButton(-1)}
            className="absolute left-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm ring-1 ring-gray-200/80 transition hover:bg-white dark:bg-gray-700/95 dark:text-gray-200 dark:ring-gray-600/80 dark:hover:bg-gray-600"
            aria-label={t('catalogFlow.filter.scrollLeft')}
          >
            <ChevronLeft className={`h-3.5 w-3.5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        )}

        <div
          ref={scrollRef}
          className={`catalog-flow-course-scope-strip flex min-w-0 w-full overflow-x-auto overflow-y-hidden scroll-smooth ${
            arrowInset ? 'px-7' : ''
          } ${fitsInTrack ? 'md:justify-center' : ''}`}
        >
          <div
            ref={pillContainerRef}
            role="tablist"
            aria-labelledby={labelId}
            className={`relative inline-flex min-h-[1.375rem] w-max max-w-none flex-nowrap items-center gap-1.5 sm:gap-2 ${
              fitsInTrack ? 'md:mx-auto' : ''
            }`}
          >
            <span id={labelId} className="sr-only">
              {t('catalogFlow.filter.filterByCourseAria')}
            </span>
            <div
              className="pointer-events-none absolute bottom-0 top-0 rounded-full shadow-sm"
              style={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
                backgroundColor: indicatorColor,
                transition:
                  'left 0.35s cubic-bezier(0.4, 0, 0.2, 1), width 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
            {items.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={item.courseId ?? 'all'}
                  ref={(el) => {
                    chipRefs.current[index] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  title={item.title}
                  onClick={() => {
                    if (!isActive) onCourseFilterChange(item.courseId);
                  }}
                  className={`relative z-10 inline-flex max-w-[8.5rem] flex-shrink-0 items-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium transition-colors duration-300 sm:max-w-[10rem] sm:px-3 sm:py-1.5 sm:text-sm ${
                    isActive
                      ? 'text-white dark:text-gray-100'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
                  }`}
                >
                  <span className="truncate">{item.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {overflow.right && (
          <button
            type="button"
            onClick={() => scrollByButton(1)}
            className="absolute right-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm ring-1 ring-gray-200/80 transition hover:bg-white dark:bg-gray-700/95 dark:text-gray-200 dark:ring-gray-600/80 dark:hover:bg-gray-600"
            aria-label={t('catalogFlow.filter.scrollRight')}
          >
            <ChevronRight className={`h-3.5 w-3.5 ${isRtl ? 'rotate-180' : ''}`} aria-hidden />
          </button>
        )}
      </div>

      <style>{`
        .catalog-flow-course-scope-strip {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }
        .catalog-flow-course-scope-strip::-webkit-scrollbar {
          height: 3px;
        }
        .catalog-flow-course-scope-strip::-webkit-scrollbar-track {
          background: transparent;
        }
        .catalog-flow-course-scope-strip::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 9999px;
        }
        .catalog-flow-course-scope-strip.scrolling {
          scrollbar-color: rgba(107, 114, 128, 0.45) transparent;
        }
        .catalog-flow-course-scope-strip.scrolling::-webkit-scrollbar-thumb {
          background: rgba(107, 114, 128, 0.45);
        }
      `}</style>
    </div>
  );
};
